import { create } from 'zustand'
import { getMatterTypes as getMatterTypesAction } from '@/actions/get-matter-types';
import { MATTER_TYPE_TYPES } from '@/constants/matter-types';
import { useSmokeballSdkStore } from './use-smokeball-sdk-store';

export interface MatterType {
  id: string;
  name: string;
  location: string;
}

type MatterTypeResponse = {
  id: string;
  name: string;
  locationId: string;
}

type GetMatterTypesFunction = (ids: string[]) => Promise<MatterType[]>

type MatterTypesStore = {
  getMatterTypes: GetMatterTypesFunction | null;
}

export const useMatterTypesStore = create<MatterTypesStore>(() => ({
  getMatterTypes: null,
}));

const cachedMatterTypes: Map<string, MatterType> = new Map();

const updateFunction = () => {
  const { isLoading, sdk, isEmbedded } = useSmokeballSdkStore.getState();
  if (!isLoading) {
    const getMatterTypes: GetMatterTypesFunction = async (ids: string[]) => {
      if (ids.length === 0) return [];

      // Filter out ids we already have in cache
      const uncachedIds = ids.filter(id => !cachedMatterTypes.has(id));
      // console.log('calling getMatterTypes cb', ids, 'uncached:', uncachedIds)
      let newMatterTypes: MatterType[] = [];

      // Only fetch if we have uncached ids
      if (uncachedIds.length > 0) {
        if (sdk) {
          const response = await sdk.matterTypes.getMany({ ids: uncachedIds });
          newMatterTypes = response.matterTypes.map(mt => mapOriginalMatterType({ matterType: mt, uncachedIds }));
        } else if (!isEmbedded) {
          const response = await getMatterTypesAction({ types: [MATTER_TYPE_TYPES.MATTER, MATTER_TYPE_TYPES.LEAD] });
          if (response?.data) {
            newMatterTypes = response.data.map(mt => ({
              id: mt.id ?? '',
              name: mt.name ?? '',
              location: mt.location ?? ''
            }));
          }
        }

        // Update cache with new matter types
        if (newMatterTypes.length > 0) {
          for (const mt of newMatterTypes) {
            cachedMatterTypes.set(mt.id, mt);
          }
        }
      }

      // Return all requested matter types (both cached and newly fetched)
      return ids.map(id => cachedMatterTypes.get(id)!).filter(Boolean);
    }
    useMatterTypesStore.setState({ getMatterTypes });
  }
}

const mapOriginalMatterType = ({ matterType }: { matterType: MatterTypeResponse, uncachedIds: string[] }) => {
  const [_, state] = matterType.id.split('_');
  const location = (matterType.locationId === 'GENERIC' || matterType.locationId === 'FED') ? state : matterType.locationId;

  return {
    id: matterType.id,
    name: matterType.name,
    location,
  }
}

const subscribeToSdk = () => {
  const unsubscribe = useSmokeballSdkStore.subscribe(() => updateFunction());

  // Return cleanup function
  return () => {
    // Unsubscribe from the store
    unsubscribe();
  };
}

export const initMatterTypesStore = () => {
  const disableSdkSub = subscribeToSdk();
  updateFunction();

  return [disableSdkSub];
}

